/*
  Bella Vista bundles: shared logic. No dependencies, no DOM rendering.
  Used by bundle-offer.js (widget) and bundle-picker.js (product picker).
  Exposes window.BVBundle.
*/
(() => {
  if (window.BVBundle && window.BVBundle.ready) return;
  const B = (window.BVBundle = window.BVBundle || {});
  B.ready = true;

  /* ---------- Text ---------- */
  const ENTITIES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  B.escape = (value) => String(value == null ? '' : value).replace(/[&<>"']/g, (char) => ENTITIES[char]);

  /* Replaces [token] placeholders, e.g. fill('Save [amount]', { amount: 'Rs.100' }). */
  B.fill = (template, tokens) =>
    String(template || '').replace(/\[(\w+)\]/g, (match, key) => (Object.prototype.hasOwnProperty.call(tokens, key) ? tokens[key] : match));

  /* ---------- Money ---------- */
  const multiCurrency = (currency) => Boolean(currency && currency.active && currency.shop && currency.active !== currency.shop);

  /* Formats cents in the customer's currency. The shop currency uses the theme's money format. */
  B.money = (cents, currency) => {
    const value = Number(cents) || 0;
    if (multiCurrency(currency)) {
      try {
        const whole = value % 100 === 0;
        return new Intl.NumberFormat(document.documentElement.lang || undefined, {
          style: 'currency',
          currency: currency.active,
          minimumFractionDigits: whole ? 0 : 2,
          maximumFractionDigits: 2
        }).format(value / 100);
      } catch (error) {
        /* Fall through to the theme format. */
      }
    }
    if (window.theme && typeof window.theme.formatMoney === 'function') return window.theme.formatMoney(value);
    return (value / 100).toFixed(2);
  };

  /* Converts a merchant-entered amount (shop currency, major units) to cents in the customer's currency. */
  B.toCents = (amount, currency) => {
    let rate = 1;
    if (multiCurrency(currency) && window.Shopify && Shopify.currency) rate = Number(Shopify.currency.rate) || 1;
    return Math.round((Number(amount) || 0) * 100 * rate);
  };

  /* ---------- Pricing ---------- */
  /*
    Groups items into cart lines (one line per variant), the way the cart will hold them.
    items: [{ id, price }] one entry per bundle item.
  */
  B.lines = (items) => {
    const lines = new Map();
    items.forEach((item) => {
      const line = lines.get(item.id);
      if (line) line.quantity += 1;
      else lines.set(item.id, { id: item.id, price: item.price, quantity: 1 });
    });
    return Array.from(lines.values());
  };

  /*
    Mirrors Shopify automatic discounts so the widget shows what checkout charges:
    - percentage:  applied to each cart line and rounded per line
    - fixed_total: once per order, never more than the subtotal
    - fixed_each:  per item, never more than the item's price
    - fixed_price: the bundle costs this amount
    tier: { type, value }, lines: output of B.lines()
  */
  B.price = (lines, tier, currency) => {
    const subtotal = lines.reduce((sum, line) => sum + line.price * line.quantity, 0);
    const value = Number(tier.value) || 0;
    let discount = 0;

    switch (tier.type) {
      case 'percentage':
        discount = lines.reduce((sum, line) => sum + Math.round((line.price * line.quantity * value) / 100), 0);
        break;
      case 'fixed_total':
        discount = B.toCents(value, currency);
        break;
      case 'fixed_each': {
        const each = B.toCents(value, currency);
        discount = lines.reduce((sum, line) => sum + Math.min(each, line.price) * line.quantity, 0);
        break;
      }
      case 'fixed_price':
        discount = subtotal - B.toCents(value, currency);
        break;
      default:
        discount = 0;
    }

    discount = Math.max(0, Math.min(Math.round(discount), subtotal));
    const total = subtotal - discount;
    const percent = subtotal > 0 ? Math.round((discount * 100) / subtotal) : 0;
    return { subtotal, discount, total, percent };
  };

  /* ---------- Variants ---------- */
  /* Products with only Shopify's "Default Title" variant have no options to pick. */
  B.isSingle = (product) =>
    Boolean(product.single) ||
    !Array.isArray(product.options) ||
    product.options.length === 0 ||
    (product.variants.length === 1 && /^default/i.test(product.variants[0].title || ''));

  /* Unique values per option, in the order Shopify lists the variants. */
  B.optionValues = (product) =>
    product.options.map((name, index) => Array.from(new Set(product.variants.map((variant) => variant.options[index]))));

  B.findVariant = (product, selected) =>
    product.variants.find((variant) => variant.options.every((value, index) => value === selected[index])) || null;

  /* Is `value` for option `index` buyable, given the other options already chosen? */
  B.valueAvailable = (product, index, value, selected) =>
    product.variants.some(
      (variant) =>
        variant.available &&
        variant.options[index] === value &&
        variant.options.every((other, otherIndex) => otherIndex === index || !selected[otherIndex] || other === selected[otherIndex])
    );

  /* Is `value` buyable in any combination? Values that never are get disabled. */
  B.valueEverAvailable = (product, index, value) =>
    product.variants.some((variant) => variant.available && variant.options[index] === value);

  /*
    Picks a variant for a product that was just placed in a bundle item.
    prefer: { names, options } from another item, so a customer who picked size M
    gets size M again when the new product has it.
  */
  B.defaultVariant = (product, prefer) => {
    const available = product.variants.filter((variant) => variant.available);
    if (prefer && Array.isArray(prefer.options) && Array.isArray(prefer.names)) {
      const match = available.find((variant) =>
        product.options.every((name, index) => {
          const preferIndex = prefer.names.indexOf(name);
          return preferIndex === -1 || prefer.options[preferIndex] === variant.options[index];
        })
      );
      if (match) return match;
    }
    return available[0] || null;
  };

  /* Keeps the value the customer just chose and adjusts the other options to a buyable variant. */
  B.resolveSelection = (product, selected, changedIndex) => {
    const exact = B.findVariant(product, selected);
    if (exact && exact.available) return exact.options.slice();
    const candidates = product.variants.filter((variant) => variant.available && variant.options[changedIndex] === selected[changedIndex]);
    if (!candidates.length) return selected.slice();
    const score = (variant) => variant.options.reduce((sum, value, index) => sum + (value === selected[index] ? 1 : 0), 0);
    candidates.sort((a, b) => score(b) - score(a));
    return candidates[0].options.slice();
  };

  /* ---------- Analytics ---------- */
  /*
    Fires a DOM event (bundle:<name>) and, when available, a Shopify customer event
    (bundle_<name>) that web pixels can subscribe to. Never throws.
  */
  B.track = (name, detail) => {
    document.dispatchEvent(new CustomEvent(`bundle:${name}`, { detail }));
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(`bundle_${name}`, detail);
      }
    } catch (error) {
      /* Analytics must never break the page. */
    }
  };

  /* ---------- Storage ---------- */
  const storage = (area) => ({
    get(key) {
      try {
        return JSON.parse(window[area].getItem(key));
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window[area].setItem(key, JSON.stringify(value));
      } catch (error) {
        /* Private mode or quota: the widget still works without persistence. */
      }
    }
  });
  B.session = storage('sessionStorage');
  B.local = storage('localStorage');

  /* ---------- Misc ---------- */
  B.uid = () => `B${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`.toUpperCase();

  const scripts = {};
  B.loadScript = (src) => {
    if (!scripts[src]) {
      scripts[src] = new Promise((resolve, reject) => {
        const script = document.createElement('script');
        script.src = src;
        script.async = true;
        script.onload = resolve;
        script.onerror = () => {
          delete scripts[src];
          reject(new Error(`Could not load ${src}`));
        };
        document.head.appendChild(script);
      });
    }
    return scripts[src];
  };
})();
